import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FaArrowLeft, FaEdit, FaExchangeAlt, FaBoxes, FaChartLine, FaWarehouse, 
  FaPlus, FaMinus, FaBarcode, FaQrcode, FaChevronDown, FaChevronUp, FaTrash
} from 'react-icons/fa';
import api from '../../utils/api';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';
import TransferModal from '../../components/inventory/TransferModal';
import UnifiedOutboundModal from '../../components/inventory/UnifiedOutboundModal';
import { fetchWarehouseProductDetails, deleteInboundRecord } from '../../actions/inventoryActions'; 

const WarehouseDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [warehouse, setWarehouse] = useState(null);
  const [inventoryDetails, setInventoryDetails] = useState({}); 
  const [detailsLoading, setDetailsLoading] = useState(new Set()); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [expandedInbounds, setExpandedInbounds] = useState(new Set());
  const [showUnifiedOutboundModal, setShowUnifiedOutboundModal] = useState(false);
  const { products, loading: productsLoading } = useSelector(state => state.warehouseProductDetails);
  const { user } = useSelector(state => state.auth);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const isSuperAdmin = user?.is_super_admin || false;

  useEffect(() => {
    fetchWarehouseDetail();
    dispatch(fetchWarehouseProductDetails(id));
  }, [id, dispatch]);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchWarehouseProductDetails(id, productSearchTerm));
    }, 500);
    
    return () => clearTimeout(timer);
  }, [productSearchTerm, id, dispatch]);

  const fetchWarehouseDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/inventory/warehouses/${id}`);
      setWarehouse(response.data.warehouse);
    } catch (err) {
      setError(err.response?.data?.message || '获取仓库详情失败');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchInventoryDetails = async (productId) => {
    // 如果已经加载过或正在加载，直接返回
    if (inventoryDetails[productId] || detailsLoading.has(productId)) {
      return;
    }
    
    try {
      setDetailsLoading(prev => new Set([...prev, productId]));
      
      const response = await api.get(`/inventory/warehouse/${id}/product/${productId}/inventory-details`);
      
      setInventoryDetails(prev => ({
        ...prev,
        [productId]: response.data
      }));
    } catch (error) {
      console.error(`获取产品${productId}详情失败:`, error);
    } finally {
      setDetailsLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('zh-CN');
  };

  const toggleProductExpansion = (productId) => {
    const newExpanded = new Set(expandedProducts);
    if (newExpanded.has(productId)) {
      newExpanded.delete(productId);
      // 收起产品时，也收起其相关的入库记录详情
      const newExpandedInbounds = new Set(expandedInbounds);
      // 找到并移除该产品相关的所有入库记录展开状态
      products?.forEach(product => {
        if (product.product_id === productId && product.batches) {
          product.batches.forEach(batch => {
            newExpandedInbounds.delete(batch.inbound_record_id);
          });
        }
      });
      setExpandedInbounds(newExpandedInbounds);
    } else {
      newExpanded.add(productId);
    }
    setExpandedProducts(newExpanded);
  };

  const toggleInboundExpansion = async (inboundId, productId) => {
    const newExpanded = new Set(expandedInbounds);
    if (newExpanded.has(inboundId)) {
      newExpanded.delete(inboundId);
    } else {
      newExpanded.add(inboundId);
      // 只在展开时加载这个特定产品的详细数据
      if (!inventoryDetails[productId]) {
        await fetchInventoryDetails(productId);
      }
    }
    setExpandedInbounds(newExpanded);
  };

  // 修复后的 handleTransfer 函数
  const handleTransfer = (box = null, product, inboundRecordId = null) => {
    console.log('handleTransfer called with:', { box, product, inboundRecordId });
    
    // 查找对应的batch信息
    let batchInfo = null;
    
    // 情况1：如果有 inboundRecordId，从 product.batches 中查找对应的batch
    if (inboundRecordId && product.batches) {
      batchInfo = product.batches.find(batch => 
        batch.inbound_record_id === inboundRecordId || batch.batch_id === inboundRecordId
      );
    }
    
    // 情况2：如果没有找到batch或没有inboundRecordId，使用第一个batch
    if (!batchInfo && product.batches && product.batches.length > 0) {
      batchInfo = product.batches[0];
    }
    
    // 情况3：如果还是没有，从product级别构造batch信息
    if (!batchInfo) {
      // 尝试从产品对象直接获取批次信息
      batchInfo = {
        inbound_record_id: product.inbound_record_id || inboundRecordId,
        batch_id: product.inbound_record_id || product.batch_id || inboundRecordId,
        remaining_units: product.remaining_units,
        total_units: product.total_units,
        unit_cost: product.unit_cost,
        units_per_box: product.units_per_box || product.unit_per_box,
        supplier: product.supplier
      };
    }
    
    // 构造 TransferModal 期望的 batch 对象
    const batch = {
      batch_id: batchInfo.batch_id || batchInfo.inbound_record_id,
      remaining_units: parseInt(batchInfo.remaining_units) || 0,
      total_units: parseInt(batchInfo.total_units) || 0,
      units_per_box: parseInt(batchInfo.units_per_box) || parseInt(product.unit_per_box) || 1,
      unit_cost: parseFloat(batchInfo.unit_cost) || 0,
      supplier: batchInfo.supplier || '未知供应商',
      product_name: product.chinese_name || product.product_name || product.name
    };
    
    // 添加调试日志
    console.log('Transfer Debug Info:', {
      originalProduct: product,
      originalBox: box,
      inboundRecordId: inboundRecordId,
      foundBatchInfo: batchInfo,
      finalBatch: batch
    });
    
    // 验证必要字段
    if (!batch.batch_id) {
      console.error('批次ID缺失，无法进行调拨', batch);
      alert('批次信息不完整，无法进行调拨操作');
      return;
    }
    
    if (batch.remaining_units <= 0) {
      alert('该批次没有剩余库存，无法调拨');
      return;
    }
    
    setSelectedBox(batch);
    setShowTransferModal(true);
  };
  
  const handleTransferSuccess = () => {
    setShowTransferModal(false);
    setSelectedBox(null);
    fetchWarehouseDetail();
    dispatch(fetchWarehouseProductDetails(id));
  };

  const handleDeleteInboundRecord = async (inboundId, productName) => {
    if (window.confirm(`确定要删除产品"${productName}"的入库记录吗？此操作无法撤销。`)) {
      try {
        await dispatch(deleteInboundRecord(inboundId));
        alert('入库记录删除成功');
        fetchWarehouseDetail();
        dispatch(fetchWarehouseProductDetails(id));
      } catch (error) {
        alert(`删除失败：${error.error || error.message}`);
      }
    }
  };
  
  if (loading) return <div className="p-6"><Loader /></div>;
  if (error) return <Message variant="error">{error}</Message>;
  if (!warehouse) return <Message variant="error">仓库不存在</Message>;
  
  return (
    <div className="warehouse-detail-page">
      {/* 页面头部 */}
      <div className="mb-6">
        <Link
          to="/admin/inventory"
          className="inline-flex items-center text-gray-600 hover:text-red-700 mb-4"
        >
          <FaArrowLeft className="mr-2" />
          返回库存管理
        </Link>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <FaWarehouse className="mr-3 text-gray-600" />
              {warehouse.name}
            </h1>
            {warehouse.description && (
              <p className="text-gray-600 mt-2">{warehouse.description}</p>
            )}
            {warehouse.address && (
              <p className="text-gray-500 mt-1">{warehouse.address}</p>
            )}
          </div>
          
          <div className="flex space-x-3">
            <Link
              to={`/admin/inventory/warehouse/${id}/edit`}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
            >
              <FaEdit className="mr-2" />
              编辑仓库
            </Link>
          </div>
        </div>
      </div>
      
      {/* 仓库统计 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">总价值</p>
              <p className="text-2xl font-bold text-green-600">
                {isSuperAdmin ? formatCurrency(warehouse.total_value) : '***'}
              </p>
            </div>
            <FaChartLine className="text-3xl text-green-600 opacity-50" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">产品种类</p>
              <p className="text-2xl font-bold">{warehouse.product_count}</p>
            </div>
            <FaBoxes className="text-2xl text-blue-600 opacity-50" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">库存数量</p>
              <p className="text-2xl font-bold">{warehouse.total_units} 瓶</p>
            </div>
            <FaWarehouse className="text-2xl text-purple-600 opacity-50" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">批次数量</p>
              <p className="text-2xl font-bold">{warehouse.batch_count}</p>
            </div>
            <FaBoxes className="text-2xl text-gray-600 opacity-50" />
          </div>
        </div>
      </div>
      
      {/* 产品库存列表 */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="border-b bg-gray-50 px-6 py-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">产品库存列表</h2>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                placeholder="搜索产品名称..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
        
        {productsLoading ? (
          <div className="p-6">
            <Loader />
          </div>
        ) : !products || products.length === 0 ? (
          <div className="p-8 text-center">
            <FaBoxes className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">该仓库暂无库存</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {(products || []).map((product) => (
              <div key={product.product_id} className="border rounded-lg overflow-hidden">
                {/* 产品头部 */}
                <div 
                  className="bg-gray-50 px-6 py-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => toggleProductExpansion(product.product_id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-16 h-16 mr-4">
                        <img
                          src={product.image_url || '/images/default-wine.jpg'}
                          alt={product.product_name}
                          className="w-full h-full object-cover rounded"
                        />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold flex items-center">
                          🍷 {product.chinese_name || product.product_name}
                        </h3>
                        {product.chinese_name && product.product_name !== product.chinese_name && (
                          <p className="text-gray-600">{product.product_name}</p>
                        )}
                        <div className="text-sm text-gray-500 mt-1">
                          总库存: <span className="font-medium text-blue-600">
                            {product.remaining_units}瓶 
                            ({Math.ceil(product.remaining_units / (product.unit_per_box || 6))}箱)
                          </span>
                          <span className="ml-4">
                            批次数: <span className="font-medium text-purple-600">{product.batch_count}</span>
                          </span>
                          <span className="ml-4">
                            总价值: <span className="font-medium text-green-600">
                            {isSuperAdmin ? formatCurrency(product.total_value) : '***'}
                          </span>
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowUnifiedOutboundModal(true);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                      >
                        <FaBarcode className="mr-2" />
                        📤 出库
                      </button>
                      {expandedProducts.has(product.product_id) ? 
                        <FaChevronUp className="text-gray-400" /> : 
                        <FaChevronDown className="text-gray-400" />
                      }
                    </div>
                  </div>
                </div>

                {/* 批次列表 */}
                {expandedProducts.has(product.product_id) && (
                  <div className="border-t">
                    {/* 为每个批次显示独立的调拨选项 */}
                    {product.batches && product.batches.map((batch, batchIndex) => (
                      <div key={batch.inbound_record_id} className="border-b last:border-b-0">
                        <div 
                          className="px-6 py-3 bg-blue-50 hover:bg-blue-100 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleInboundExpansion(batch.inbound_record_id, product.product_id);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="text-sm font-medium">
                                📦 批次#{batch.inbound_record_id} - {formatDate(batch.created_at)}
                              </span>
                              <span className="ml-4 text-xs text-gray-600">
                                剩余: {batch.remaining_units}瓶 | 供应商: {batch.supplier} | 
                                成本: {isSuperAdmin ? formatCurrency(batch.unit_cost) : '***'}/瓶
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTransfer(null, product, batch.inbound_record_id);
                                }}
                                className="text-orange-600 hover:text-orange-800 text-sm px-3 py-1 border border-orange-300 rounded hover:bg-orange-50"
                                disabled={batch.remaining_units <= 0}
                              >
                                <FaExchangeAlt className="mr-1" />
                                调拨 ({batch.remaining_units}瓶)
                              </button>
                              {isSuperAdmin && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteInboundRecord(batch.inbound_record_id, product.product_name);
                                  }}
                                  className="text-red-600 hover:text-red-800 text-sm px-3 py-1 border border-red-300 rounded hover:bg-red-50"
                                  title="删除此批次"
                                >
                                  <FaTrash className="mr-1" />
                                  删除
                                </button>
                              )}
                              {expandedInbounds.has(batch.inbound_record_id) ? 
                                <><FaChevronUp className="inline mr-1" />收起</> : 
                                <><FaChevronDown className="inline mr-1" />详情</>
                              }
                            </div>
                          </div>
                        </div>

                        {/* 箱码和单元码详情展示 */}
                        {expandedInbounds.has(batch.inbound_record_id) && (
                          <div className="px-6 py-4 bg-gray-50">
                            <h4 className="text-lg font-semibold mb-4 flex items-center">
                              <FaBarcode className="mr-2 text-blue-600" />
                              条码信息详情
                            </h4>
                            
                            {/* 显示所有箱信息 */}
                            <div className="space-y-4">
                              {detailsLoading.has(product.product_id) ? (
                                <div className="text-center py-4">
                                  <Loader />
                                  <span className="ml-2 text-sm text-gray-500">加载详情中...</span>
                                </div>
                              ) : inventoryDetails[product.product_id] && inventoryDetails[product.product_id].length > 0 ? (
                                inventoryDetails[product.product_id]
                                  .filter(detailItem => detailItem.inbound_record_id === batch.inbound_record_id)
                                  .map((detailItem) => (
                                  <div key={detailItem.inbound_record_id}>
                                    {detailItem.boxes && detailItem.boxes.map((box, boxIndex) => (
                                      <div key={box.box_id} className="border rounded-lg p-4 bg-white">
                                        <div className="flex justify-between items-start mb-3">
                                          <div>
                                            <h5 className="font-medium text-gray-800">
                                              📦 第{box.box_index}箱 
                                              <span className="ml-2 text-sm text-gray-600">
                                                ({box.remaining_units}/{box.units_count} 剩余)
                                              </span>
                                            </h5>
                                          </div>
                                          <span className={`px-2 py-1 rounded-full text-xs ${
                                            box.status === 'full' ? 'bg-green-100 text-green-800' :
                                            box.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-gray-100 text-gray-800'
                                          }`}>
                                            {box.status === 'full' ? '满箱' : 
                                            box.status === 'partial' ? '散货' : '已空'}
                                          </span>
                                        </div>
                                        
                                        {/* 箱码信息 - 只有非散货才显示箱码 */}
                                        {box.status !== 'partial' && (
                                          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded">
                                            <h6 className="text-sm font-medium text-blue-800 mb-2">📦 箱码信息</h6>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                                              {box.box_barcode && (
                                                <div className="flex items-center">
                                                  <FaBarcode className="mr-2 text-blue-500" />
                                                  <span className="font-mono">{box.box_barcode}</span>
                                                </div>
                                              )}
                                              {box.box_qrcode && (
                                                <div className="flex items-center">
                                                  <FaQrcode className="mr-2 text-green-500" />
                                                  <span className="font-mono">{box.box_qrcode}</span>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* 单元码信息 */}
                                        {box.units && box.units.length > 0 && (
                                          <div className="p-3 bg-gray-50 border border-gray-200 rounded">
                                            <h6 className="text-sm font-medium text-gray-800 mb-2">🍷 单元码信息</h6>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                              {box.units.map((unit) => (
                                                <div key={unit.unit_id} className="p-2 bg-white border rounded text-xs">
                                                  <div className="flex justify-between items-center mb-1">
                                                    <span className="font-medium">第{unit.unit_index}瓶</span>
                                                    <span className={`px-1 py-0.5 rounded text-xs ${
                                                      unit.status === 'available' ? 'bg-green-100 text-green-700' :
                                                      unit.status === 'sold' ? 'bg-red-100 text-red-700' :
                                                      'bg-gray-100 text-gray-700'
                                                    }`}>
                                                      {unit.status === 'available' ? '可用' : 
                                                      unit.status === 'sold' ? '已售' : '不可用'}
                                                    </span>
                                                  </div>
                                                  
                                                  {unit.unit_barcode && (
                                                    <div className="flex items-center mb-1">
                                                      <FaBarcode className="mr-1 text-blue-400 text-xs" />
                                                      <span className="font-mono truncate">{unit.unit_barcode}</span>
                                                    </div>
                                                  )}
                                                  
                                                  {unit.unit_qrcode && (
                                                    <div className="flex items-center mb-1">
                                                      <FaQrcode className="mr-1 text-green-400 text-xs" />
                                                      <span className="font-mono truncate">{unit.unit_qrcode}</span>
                                                    </div>
                                                  )}
                                                  
                                                  {!unit.unit_barcode && !unit.unit_qrcode && (
                                                    <div className="text-gray-400 italic">暂无码信息</div>
                                                  )}
                                                  
                                                  {unit.sold_at && (
                                                    <div className="text-gray-400 text-xs mt-1">
                                                      售出时间: {new Date(unit.sold_at).toLocaleDateString()}
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ))
                              ) : (
                                <div className="text-center py-4 text-gray-500">
                                  暂无详细信息
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* 调拨模态框 */}
      {showTransferModal && selectedBox && (
        <TransferModal
          batch={selectedBox}
          fromWarehouseId={parseInt(id)}
          fromWarehouseName={warehouse.name}
          onClose={() => {
            setShowTransferModal(false);
            setSelectedBox(null);
          }}
          onSuccess={handleTransferSuccess}
        />
      )}
      
      {/* 扫瓶码出库模态框 */}
      {showUnifiedOutboundModal && (
        <UnifiedOutboundModal 
          onClose={() => setShowUnifiedOutboundModal(false)}
          onSuccess={() => {
            setShowUnifiedOutboundModal(false);
            fetchWarehouseDetail();
            dispatch(fetchWarehouseProductDetails(id));
          }}
        />
      )}
    </div>
  );
};

export default WarehouseDetailPage;